import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand/vanilla';
import type { SharingApi } from '../api';
import { sharingApi } from '../api';
import { Poller } from '../model/poller';
import type { NotificationView } from '../model/notifications';
import { markRead, toView } from '../model/notifications';

export const NOTIFICATIONS_POLL_MS = 30_000;

export interface NotificationsState {
  status: 'idle' | 'ready' | 'error';
  items: NotificationView[];
}

/** In-app notifications (decision Q7): polled every 30 s while a bell is mounted. */
export class NotificationsService {
  readonly store: StoreApi<NotificationsState> = createStore<NotificationsState>()(() => ({
    status: 'idle',
    items: [],
  }));
  private readonly poller: Poller;
  private refs = 0;

  constructor(
    private readonly api: () => SharingApi = () => sharingApi(true),
    intervalMs = NOTIFICATIONS_POLL_MS,
  ) {
    this.poller = new Poller({ run: () => this.refresh(), intervalMs });
  }

  acquire(): () => void {
    this.refs += 1;
    if (this.refs === 1) void this.poller.start();
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.refs -= 1;
      if (this.refs === 0) this.poller.stop();
    };
  }

  poke(): void {
    void this.poller.trigger();
  }

  async refresh(): Promise<void> {
    try {
      const list = await this.api().listNotifications();
      this.store.setState({ status: 'ready', items: list.map(toView) });
    } catch (err) {
      if (this.store.getState().status !== 'ready') this.store.setState({ status: 'error' });
      throw err;
    }
  }

  async markRead(ids: readonly string[] | 'all'): Promise<void> {
    const before = this.store.getState().items;
    this.store.setState({ items: markRead(before, ids) });
    try {
      await this.api().markNotificationsRead(ids === 'all' ? { all: true } : { ids: [...ids] });
    } catch {
      // Keep the optimistic state; the next poll corrects it.
    }
  }
}

export const notifications = new NotificationsService();
